import type { ResultRow } from '@clockwise/semantic-core';

/** One employee, as a row of the grid. */
export interface SkillMatrixEmployee {
  key: string;
  label: string;
}

/** A fixed row sourced from the Utilization model — hours, not a rating. */
export interface SkillMatrixMetricRow {
  kind: 'metric';
  key: 'availableHours';
  label: string;
  /** Employee key → hours this month, or `null` where Utilization has no row for them. */
  values: Record<string, number | null>;
}

/** One row per skill rated by somebody in scope. */
export interface SkillMatrixSkillRow {
  kind: 'skill';
  key: string;
  label: string;
  /** 'skill' or 'application' — which proficiency scale to read the levels
   *  below through (`PROFICIENCY_LEVEL_LABELS`), since the two name level 2
   *  differently. */
  skillType: 'skill' | 'application';
  /** Employee key → their raw proficiency level ('0'–'5'), or `null` if unrated. */
  values: Record<string, string | null>;
}

export interface SkillMatrixGrid {
  /** Every employee the grid has ANYTHING to say about, alphabetical by
   *  name — one ROW each; `metricRows`/`skillRows` are the COLUMNS, each
   *  holding its values keyed by the employee key here. */
  employees: SkillMatrixEmployee[];
  /** Employee key → their `users.location` (blank until set on their profile),
   *  read off Utilization's row — every active employee has one, where
   *  `skillMatrix` only carries a row for someone already rated. */
  locationByEmployee: Record<string, string | null>;
  /** Employee key → their email. Read off the SKILLS query instead of
   *  Utilization's — `employeeEmail` is a `skillMatrix`-only dimension — so
   *  it's blank for an employee with a capacity row but no rating yet,
   *  same gap Location doesn't have but skill cells already do. */
  emailByEmployee: Record<string, string | null>;
  /** Always exactly Available Hours — one entry, but kept as an array for
   *  the same reason `skillRows` is: the view renders both the same way. */
  metricRows: SkillMatrixMetricRow[];
  /** One entry per distinct rated skill, alphabetical by name. */
  skillRows: SkillMatrixSkillRow[];
}

const METRICS: ReadonlyArray<{ key: SkillMatrixMetricRow['key']; label: string }> = [
  { key: 'availableHours', label: 'Available Hours' },
];

/**
 * Transposes two flat query results — `skillMatrix` (detail) and
 * `utilization` (pivot by employee, current month) — into one grid with
 * employees as rows and their skills/hours/location as columns. Both
 * results share the same flat `{ dims, values }` shape
 * (`packages/semantic-core/src/result.ts`), which is what makes merging
 * them a client-side join on `dims.employee.key` rather than anything the
 * server needs to know how to do.
 *
 * The employee set is the UNION of both results' employees, not just
 * Utilization's: an employee rated on a skill but missing a capacity row for
 * some edge reason (no `hoursPerMonth` set, say) still gets a row rather
 * than silently losing their ratings off the grid.
 */
export function buildSkillMatrixGrid(
  skillQueryRows: readonly ResultRow[],
  utilizationQueryRows: readonly ResultRow[],
): SkillMatrixGrid {
  const employeeLabels = new Map<string, string>();
  for (const row of utilizationQueryRows) {
    const employee = row.dims.employee;
    if (employee?.key == null) continue;
    employeeLabels.set(employee.key, employee.label ?? employee.key);
  }
  for (const row of skillQueryRows) {
    const employee = row.dims.employee;
    if (employee?.key == null || employeeLabels.has(employee.key)) continue;
    employeeLabels.set(employee.key, employee.label ?? employee.key);
  }
  const employees = Array.from(employeeLabels, ([key, label]) => ({ key, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  const locationByEmployee: Record<string, string | null> = {};
  for (const row of utilizationQueryRows) {
    const employee = row.dims.employee;
    if (employee?.key == null) continue;
    const location = row.dims.location;
    locationByEmployee[employee.key] = location?.label ?? location?.key ?? null;
  }

  const emailByEmployee: Record<string, string | null> = {};
  for (const row of skillQueryRows) {
    const employee = row.dims.employee;
    if (employee?.key == null) continue;
    const email = row.dims.employeeEmail;
    emailByEmployee[employee.key] = email?.label ?? email?.key ?? null;
  }

  const metricRows = METRICS.map(({ key, label }) => {
    const values: Record<string, number | null> = {};
    for (const row of utilizationQueryRows) {
      const employee = row.dims.employee;
      if (employee?.key == null) continue;
      values[employee.key] = row.values[key] ?? null;
    }
    return { kind: 'metric' as const, key, label, values };
  });

  const skillsByKey = new Map<
    string,
    { label: string; skillType: 'skill' | 'application'; values: Record<string, string | null> }
  >();
  for (const row of skillQueryRows) {
    const skill = row.dims.skill;
    const employee = row.dims.employee;
    if (skill?.key == null || employee?.key == null) continue;
    let entry = skillsByKey.get(skill.key);
    if (!entry) {
      const type = row.dims.skillType?.key;
      entry = {
        label: skill.label ?? skill.key,
        skillType: type === 'application' ? 'application' : 'skill',
        values: {},
      };
      skillsByKey.set(skill.key, entry);
    }
    const level = row.dims.proficiencyLevel;
    entry.values[employee.key] = level?.key ?? null;
  }
  const skillRows = Array.from(skillsByKey, ([key, { label, skillType, values }]) => ({
    kind: 'skill' as const,
    key,
    label,
    skillType,
    values,
  })).sort((a, b) => a.label.localeCompare(b.label));

  return { employees, locationByEmployee, emailByEmployee, metricRows, skillRows };
}
